import { useState } from "react";
import { CalendarDays, Plus } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/control/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, RowLink, ActionButton, SelectField, TextField, useLoad, fmt, can, COUNTRY_OPTIONS,
} from "@/components/control/kit";
import { activityFields, activityBody, categoryLabel } from "@/components/control/activityFields";
import { ActivityStepper } from "@/components/control/ActivityPages";

const TABS = [["all", "Alle"], ["entwurf", "Entwurf"], ["zur_pruefung", "Zur Prüfung"], ["veröffentlicht", "Veröffentlicht"],
  ["abgesagt", "Abgesagt"], ["archiviert", "Archiviert"]];

function orgFields(canOrgs, orgs, preset) {
  if (!canOrgs) return [];
  const list = orgs.data?.organizations || [];
  const valid = list.some((o) => o.id === preset);
  const notes = [];
  if (orgs.error) notes.push({ type: "info", text: `Organisationen konnten nicht geladen werden: ${orgs.error}` });
  else if (preset && !valid) notes.push({ type: "info", text: "Die Organisation aus dem Link ist nicht freigegeben, nicht vorhanden oder für dich nicht sichtbar. Sie wurde nicht übernommen." });
  return [...notes, { name: "organization_id", label: "Organisation (optional)", type: "select", placeholder: "Keine Organisation",
    options: list.map((o) => ({ value: o.id, label: o.name })), default: valid ? preset : "",
    help: "Nur freigegebene Organisationen in deinen Ländern. Der Server prüft die Auswahl erneut." }];
}

function CreateActivity() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const preset = params.get("organisation");
  const canOrgs = can(user, "organizations:view");
  const orgs = useLoad(canOrgs ? "/organizations?status=freigegeben" : null);
  const waiting = canOrgs && orgs.loading;
  const org = (orgs.data?.organizations || []).find((o) => o.id === preset);
  return (
    <ActionButton key={preset && !waiting ? "preset" : "plain"} label="Aktivität anlegen" icon={Plus} variant="default" testId="create-activity"
      page="neu" title="Aktivität anlegen: Details" pageSubtitle="Erstelle einen Entwurf mit Termin, öffentlichem Ort und klaren Kosten."
      pageTop={<ActivityStepper current="bearbeiten" />}
      submitLabel="Weiter zu Zugang" defaultOpen={!!preset && !waiting} disabled={!!preset && waiting}
      description="Die Aktivität wird als Entwurf gespeichert. Zugangsangaben ergänzt du im nächsten Schritt."
      fields={[...orgFields(canOrgs, orgs, preset), ...activityFields({ country: org?.country })]}
      onSubmit={async (v) => {
        const { data } = await api.post("/manage/activities", activityBody(v));
        navigate(`/aktivitaeten/${data.activity.id}/zugang`, { replace: true });
        return "Entwurf gespeichert.";
      }} />
  );
}

export default function Activities() {
  const [status, setStatus] = useState("all");
  const [country, setCountry] = useState("all");
  const [q, setQ] = useState("");
  const st = useLoad(`/manage/activities?${new URLSearchParams({ status, country, q })}`);
  const counts = st.data?.counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const tabs = useTabs(TABS.map(([value, label]) => ({ value, label, count: value === "all" ? total : counts[value] || 0 })), status);

  return (
    <div>
      <PageHeader title="Aktivitäten" subtitle="Aktivitäten anlegen, Zugang klären, freigeben und Teilnahmen verwalten." icon={CalendarDays}
        actions={<>
          <Link to="/aktivitaeten/zugangsluecken" data-testid="access-gaps-link" className="rounded-lg border border-brand-purple/30 bg-white px-3 py-2 text-sm font-semibold text-brand-purple hover:bg-secondary">Fehlende Zugangsangaben</Link>
          {st.data?.can.manage && <CreateActivity />}
        </>} />
      <Tabbar tabs={tabs} onChange={setStatus} testId="activities-status-tabs" />
      <div className="mb-5 grid gap-3 rounded-xl border border-border bg-white p-4 sm:grid-cols-[1fr_200px]">
        <TextField id="act-q" label="Titel" value={q} onChange={setQ} testId="activities-search" />
        <SelectField id="act-country" label="Land" value={country} onChange={setCountry} testId="activities-country"
          options={[{ value: "all", label: "Alle Länder" }, ...COUNTRY_OPTIONS]} />
      </div>
      <TabPanel tabs={tabs} testId="activities-status-tabs-panel">
      <Section title="Aktivitätenliste" testId="activities-section">
        <Loadable state={st}>
          {(d) => (
            <div className="overflow-x-auto">
              <Table data-testid="activities-table">
                <TableHeader><TableRow>
                  <TableHead>Titel</TableHead><TableHead>Kategorie</TableHead><TableHead>Termin</TableHead><TableHead>Treffpunkt</TableHead>
                  <TableHead>Teilnahmen</TableHead><TableHead>Zugang offen</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {d.activities.length === 0 && <TableRow><TableCell colSpan={7}><Empty /></TableCell></TableRow>}
                  {d.activities.map((a) => (
                    <TableRow key={a.id} data-testid={`activity-row-${a.id}`}>
                      <TableCell><RowLink to={`/aktivitaeten/${a.id}`} testId={`activity-link-${a.id}`}>{a.title_de}</RowLink></TableCell>
                      <TableCell>{categoryLabel(a.category_key)}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{fmt(a.date, a.timezone)}</TableCell>
                      <TableCell className="text-sm">{a.location}</TableCell>
                      <TableCell className="text-sm">{a.participants_count ?? 0}{a.capacity ? ` / ${a.capacity}` : ""}</TableCell>
                      <TableCell className="text-sm">{a.access ? a.access.open_count : "nicht erfasst"}</TableCell>
                      <TableCell><Status value={a.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Loadable>
      </Section>
      </TabPanel>
    </div>
  );
}
